import { useState, useEffect, useRef } from "react";
import { Firebase, fbAuth } from "../../services/firebaseService/connection";
import useUserStore, { selectUserDetails } from "../../store/user/user";
import {
  fbRegisterAdmin,
  fbLogoutUser,
} from "../../services/firebaseService/endPoints/user";
import { startUpdateUser } from "../../redux/actions/user";
import fakeDispatch from "../../utils/fakeDispatch";
import LoadingModal from "../commons/LoadingModal";
import { message } from "antd";
import MobileVerification from "../commons/MobileVerification";
import { withRouter } from "../../utils/helper";

const AdminRegister = ({ router }) => {
  const user = useUserStore(selectUserDetails);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [showAlert, setShowAlert] = useState({ show: false, type: "success", message: "" });
  const [linkVerified, setLinkVerified] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDocument, setLoadingDocument] = useState(true);
  const confirmResultRef = useRef(null);
  const otpRef = useRef(null);

  // getDerivedStateFromProps equivalent: set loadingDocument false when user is unauthenticated
  useEffect(() => {
    if (user.isAuthenticated === false) {
      setLoadingDocument(false);
    }
  }, [user.isAuthenticated]);

  // componentDidMount equivalent
  useEffect(() => {
    if (user.isAuthenticated) {
      fbLogoutUser();
    }
    return () => {
      message.destroy();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // componentDidUpdate equivalent: fires once when loadingDocument becomes false and linkVerified is still undefined
  useEffect(() => {
    if (!isLoading && loadingDocument === false && linkVerified === undefined) {
      setIsLoading(true);
      if (Firebase.auth().isSignInWithEmailLink(window.location.href)) {
        Firebase.auth()
          .signInWithEmailLink(router.params.email, window.location.href)
          .then(() => {
            const name = "phoneNumber";
            const escapedName = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
            const regex = new RegExp("[\\?&]" + escapedName + "=([^&#]*)");
            const results = regex.exec(window.location.search);
            const phone =
              results === null
                ? ""
                : decodeURIComponent(results[1].replace(/\+/g, " "));
            setLinkVerified(true);
            setIsLoading(false);
            setPhoneNumber(phone ? `+${phone}` : "");
          })
          .catch((err) => {
            setLinkVerified(false);
            setShowAlert({ show: true, type: "error", message: err.message });
            setIsLoading(false);
          });
      } else {
        setLinkVerified(false);
        setShowAlert({ show: true, type: "error", message: "Invalid link" });
        setIsLoading(false);
      }

      message.success(
        "Do not refresh or close the window until the registration is complete",
        10
      );
    }
  }, [isLoading, loadingDocument, linkVerified]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerifyOtp = () => {
    setShowAlert({ show: false });
    const prevUser = fbAuth.currentUser;
    if (prevUser) {
      setIsLoading(true);
      confirmResultRef.current
        ?.confirm(otpRef.current)
        .then((result) => {
          const email = prevUser.email;
          prevUser.delete().then(() => {
            fbRegisterAdmin(email, result.additionalUserInfo.isNewUser)
              .then(() => {
                setIsLoading(false);
                fakeDispatch(startUpdateUser());
                fakeDispatch({ type: "LOADING_USER" });
                router.navigate("/admin/content", { replace: true });
              })
              .catch((err) => {
                setShowAlert({ show: true, type: "error", message: err.message });
                setIsLoading(false);
              });
          });
        })
        .catch(() => {
          setIsLoading(false);
          setShowAlert({
            show: true,
            type: "error",
            message:
              "The verification code you entered is incorrect. Please check the code and try again. If you entered an incorrect phone number you can edit the number.",
          });
        });
    } else {
      setShowAlert({
        show: true,
        type: "error",
        message: "Email authentication failed",
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      <LoadingModal isLoading={isLoading || loadingDocument} />
      {!linkVerified && !isLoading && showAlert.show && (
        <div className="container mt-5">
          <div className="alert alert-danger" role="alert">
            {showAlert.message}
          </div>
        </div>
      )}
      {linkVerified && (
        <MobileVerification
          handleVerifyOtp={handleVerifyOtp}
          phoneNumber={phoneNumber}
        />
      )}
    </>
  );
};

export default withRouter(AdminRegister);
